import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, X, ArrowRight, Infinity as InfinityIcon } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Rich content shown below the pricing cards on /precios:
 *   1. A per-plan feature comparison table (driven by the same /api/plans data
 *      as the cards, so it never drifts from real prices/limits).
 *   2. A generalised "Zettapost vs otras herramientas" value comparison.
 *   3. A pricing FAQ.
 */

type Plan = {
  id: string;
  name: string;
  priceMonthly: number;
  unlimitedAccounts: boolean;
  aiPredictionsMonthly: number;
  aiCaptionsMonthly: number;
  aiIdeasMonthly: number;
  bioPagesLimit: number | null;
  teamMembersLimit: number | null;
  networks: { platform: string; maxAccounts: number }[];
};

/** A value shown in a comparison cell: a checkmark, a cross, or free text. */
type CellValue = { kind: 'check' } | { kind: 'cross' } | { kind: 'text'; text: string };

const CHECK: CellValue = { kind: 'check' };
const txt = (text: string): CellValue => ({ kind: 'text', text });

/** Per-plan value for a numeric limit row. */
function accountsPerNetwork(p: Plan): CellValue {
  if (p.unlimitedAccounts) return txt('∞');
  const n = p.networks[0]?.maxAccounts ?? 1;
  return txt(String(n));
}
function bioPages(p: Plan): CellValue {
  return txt(p.bioPagesLimit === null ? '∞' : String(p.bioPagesLimit));
}
function teamSeats(p: Plan): CellValue {
  if (p.teamMembersLimit === null) return txt('∞');
  if (p.teamMembersLimit === 0) return { kind: 'cross' };
  return txt(`${p.teamMembersLimit}`);
}

type Row = { label: string; get: (p: Plan) => CellValue };
type Group = { title: string; rows: Row[] };

const GROUPS: Group[] = [
  {
    title: 'Publicación',
    rows: [
      { label: 'Redes sociales', get: () => txt('9 redes') },
      { label: 'Publicaciones programadas', get: () => txt('Ilimitadas') },
      { label: 'Calendario visual + cola inteligente', get: () => CHECK },
      { label: 'Previsualización real por red', get: () => CHECK },
      { label: 'Formatos nativos (Reels, Stories, Shorts, Pins)', get: () => CHECK },
    ],
  },
  {
    title: 'Límites por plan',
    rows: [
      { label: 'Cuentas por red', get: accountsPerNetwork },
      { label: 'Páginas de Link in bio', get: bioPages },
      { label: 'Miembros de equipo', get: teamSeats },
    ],
  },
  {
    title: 'Asistente de IA',
    rows: [
      { label: 'Ideas de contenido', get: (p) => txt(`${p.aiIdeasMonthly}/mes`) },
      { label: 'Captions con IA', get: (p) => txt(`${p.aiCaptionsMonthly}/mes`) },
      { label: 'Predicción de rendimiento', get: (p) => txt(`${p.aiPredictionsMonthly}/mes`) },
    ],
  },
  {
    title: 'Incluido en todos los planes',
    rows: [
      { label: 'Analítica unificada de tus redes', get: () => CHECK },
      { label: 'Aprobación de clientes por enlace', get: () => CHECK },
      { label: 'API + publicar desde ChatGPT', get: () => CHECK },
      { label: 'Soporte en español', get: () => CHECK },
      { label: 'Sin permanencia — cancela cuando quieras', get: () => CHECK },
    ],
  },
];

function Cell({ value }: { value: CellValue }) {
  if (value.kind === 'check') return <Check size={17} className="mx-auto text-emerald-600" />;
  if (value.kind === 'cross') return <X size={16} className="mx-auto text-slate-300" />;
  const isInfinity = value.text === '∞';
  return (
    <span className="font-semibold text-slate-800 tabular-nums">
      {isInfinity ? <InfinityIcon size={16} className="mx-auto text-brand-600" /> : value.text}
    </span>
  );
}

function PlanFeatureTable() {
  const { data } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ plans: Plan[] }>('/api/plans'),
  });
  const plans = data?.plans ?? [];
  if (plans.length === 0) return null;

  return (
    <section className="mt-20">
      <div className="text-center max-w-xl mx-auto mb-8">
        <h2 className="text-2xl font-extrabold text-slate-900">Qué incluye cada plan</h2>
        <p className="mt-2 text-slate-500">
          Todos los planes traen lo esencial para publicar en 9 redes. Suben las cuentas, los equipos y la IA.
        </p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left font-medium text-slate-400 px-5 py-4 w-[38%]">Características</th>
              {plans.map((p) => (
                <th key={p.id} className="px-4 py-4 text-center">
                  <span className="block font-bold text-slate-900">{p.name}</span>
                  <span className="block text-xs font-medium text-slate-400 mt-0.5">
                    {(p.priceMonthly / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}/mes
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GROUPS.map((group) => (
              <Fragment key={group.title}>
                <tr className="bg-slate-50/70">
                  <td
                    colSpan={plans.length + 1}
                    className="px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                  >
                    {group.title}
                  </td>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.label} className="border-t border-slate-100">
                    <td className="px-5 py-3 text-slate-700">{row.label}</td>
                    {plans.map((p) => (
                      <td key={p.id} className="px-4 py-3 text-center">
                        <Cell value={row.get(p)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── Zettapost vs otras herramientas ─────────────────────── */

type CompRow = { feature: string; zettapost: CellValue; others: CellValue };

const COMPETITOR_ROWS: CompRow[] = [
  { feature: 'Precio de inicio', zettapost: txt('Desde 9,99 €/mes'), others: txt('~25–99 €/mes') },
  { feature: 'IA (ideas, captions, predicción)', zettapost: txt('Incluida'), others: txt('Extra o plan superior') },
  { feature: '9 redes incluidas', zettapost: CHECK, others: txt('A menudo por canal') },
  { feature: 'Interfaz y soporte en español', zettapost: CHECK, others: txt('Normalmente solo inglés') },
  { feature: 'Aprobación de clientes', zettapost: CHECK, others: txt('Solo planes altos') },
  { feature: 'Sin permanencia', zettapost: CHECK, others: txt('Varía') },
];

function CompetitorComparison() {
  return (
    <section className="mt-20">
      <div className="text-center max-w-xl mx-auto mb-8">
        <h2 className="text-2xl font-extrabold text-slate-900">Zettapost vs otras herramientas</h2>
        <p className="mt-2 text-slate-500">
          Lo mismo (o más), más simple, en español y por menos.
        </p>
      </div>

      <div className="max-w-3xl mx-auto overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left font-medium text-slate-400 px-5 py-4"></th>
              <th className="px-4 py-4 text-center font-bold text-brand-700 bg-brand-50/50">Zettapost</th>
              <th className="px-4 py-4 text-center font-medium text-slate-500">Otras herramientas</th>
            </tr>
          </thead>
          <tbody>
            {COMPETITOR_ROWS.map((r) => (
              <tr key={r.feature} className="border-t border-slate-100">
                <td className="px-5 py-3 font-medium text-slate-700">{r.feature}</td>
                <td className="px-4 py-3 text-center bg-brand-50/30"><Cell value={r.zettapost} /></td>
                <td className="px-4 py-3 text-center text-slate-500">
                  {r.others.kind === 'text' ? r.others.text : <Cell value={r.others} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="max-w-3xl mx-auto mt-3 text-xs text-slate-400 text-center">
        Comparativa orientativa; los precios de la competencia son planes de entrada aproximados y pueden variar.
        Consulta las{' '}
        <Link to="/alternativas" className="text-brand-600 font-medium hover:underline">
          comparativas detalladas por herramienta
        </Link>
        .
      </p>
    </section>
  );
}

/* ── FAQ de precios ──────────────────────────────────────── */

const FAQS: { q: string; a: string }[] = [
  {
    q: '¿Hay permanencia o compromiso?',
    a: 'No. Todos los planes son de pago recurrente sin permanencia: puedes cancelar cuando quieras desde tu cuenta y seguirás teniendo acceso hasta el final del periodo ya pagado.',
  },
  {
    q: '¿Cómo funciona la prueba de 3 días gratis?',
    a: 'Te pedimos los datos de pago para validar la tarjeta, pero no te cobramos nada durante los 3 días. Si cancelas antes de que terminen, no se te cobra. Pasado ese plazo se cobra la primera cuota del plan elegido.',
  },
  {
    q: '¿Puedo cambiar de plan más adelante?',
    a: 'Sí. Puedes subir o bajar de plan cuando quieras. Al cambiar, se ajusta tu suscripción al nuevo plan y sus límites de cuentas, equipo e IA.',
  },
  {
    q: '¿Qué métodos de pago aceptáis?',
    a: 'Aceptamos pago con tarjeta a través de nuestra pasarela segura. La primera cuota se cobra al terminar la prueba y después se renueva de forma automática según el periodo (mensual o anual) que elijas.',
  },
  {
    q: '¿Qué diferencia hay entre pago mensual y anual?',
    a: 'El plan anual sale más barato que pagar doce meses sueltos. Puedes ver el ahorro exacto en cada tarjeta cambiando el selector a "Anual".',
  },
  {
    q: '¿Qué pasa si supero los límites de IA de mi plan?',
    a: 'Los créditos de IA (ideas, captions y predicciones) se renuevan cada mes. Si necesitas más, el siguiente plan multiplica esas cuotas; puedes subir de plan en cualquier momento.',
  },
];

function PricingFaq() {
  return (
    <section className="mt-20 max-w-3xl mx-auto">
      <h2 className="text-2xl font-extrabold text-slate-900 text-center mb-8">Preguntas frecuentes</h2>
      <div className="space-y-3">
        {FAQS.map((f) => (
          <details key={f.q} className="group rounded-xl border border-slate-200 bg-white open:shadow-sm">
            <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-slate-900 flex items-center justify-between gap-3">
              {f.q}
              <ArrowRight
                size={16}
                className="shrink-0 text-slate-400 transition-transform duration-200 group-open:rotate-90"
              />
            </summary>
            <p className="px-5 pb-5 -mt-1 text-slate-600 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ── Final CTA band ──────────────────────────────────────── */

function PricingCta() {
  return (
    <section className="mt-20 text-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 text-white px-6 py-12">
      <h2 className="text-2xl sm:text-3xl font-extrabold">Empieza hoy, cancela cuando quieras</h2>
      <p className="mt-2 text-white/90 max-w-lg mx-auto">
        Prueba Zettapost 3 días gratis. Programa tu primera semana de contenido en minutos.
      </p>
      <Link
        to="/register"
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3 font-semibold text-brand-700 hover:bg-white/90 transition group"
      >
        Crear cuenta gratis
        <ArrowRight size={18} className="transition group-hover:translate-x-1" />
      </Link>
    </section>
  );
}

export function PricingComparison() {
  return (
    <>
      <PlanFeatureTable />
      <CompetitorComparison />
      <PricingFaq />
      <PricingCta />
    </>
  );
}
